import { useRouter } from 'next/router';
import useForm from 'rc-form-hooks';
import { useEffect, useRef, useState } from 'react';
import { afterUserRegisterLogin, afterUserSign } from '@/utils/common/pixel';
import { getReferralIdFromCookie, getReferrerId } from '@/utils/common/referral';
import { getCookie } from '@/utils/cookie';
import md5 from '@/utils/md5';
import { loadGoogloRecaptcha } from '@/utils/recaptcha';
import { InvisibleFilled, VisibleFilled } from '@weee-fe/kunlunicon';
import StudentInputItem from '@/components/student-discount/StudentSignupForm/StudentInputItem';
import { LoginSessionstoragekey } from '@/constants/account';
import { AUTH_SIGN } from '@/constants/auth_sign';
import Constants from '@/constants/const';
import Button from '@/components/common/Button';
import useTranslation from 'next-translate/useTranslation';
import { emailRegisterByStudent } from '@/api/customer';
import { buildUrlWithOrigin } from '@/utils/url';
import { twMerge } from 'tailwind-merge';
import { EDU_EMAIL_REG } from '@/constants/reg_rules';
import { getSafeRedirectUrl } from '@/utils/safeRedirect';
import VerifyEmailModal from '@/components/homepage/components/PopupCreateAccount/VerifyEmailModal';
import { isSayWeeeApp } from '@/utils/common/device';

export const StudentSignUpForm = props => {
  const { formStyle, schoolData, className, clickTrack, onSuccess, entry = '', reqParams = {} } = props;
  const { t, lang } = useTranslation('common');
  const router = useRouter();
  const { getFieldDecorator, values, errors } = useForm();
  const { email, password } = values;
  const [encodePassword, setEncodePassword] = useState<boolean>(true);
  const [errorRes, setErrorRes] = useState<string>('');
  const [passwordError, setPasswardError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState<string>('1');
  const submitButtonDisabled = !email || !password || Object.keys(errors)?.length > 0;

  const codeRef = useRef<any>(null);

  const handleBlur = () => {
    if (!!document.querySelector('#terms')) (document.querySelector('#terms') as HTMLElement).style.display = `block`;
  };
  const handleFocus = () => {
    if (!!document.querySelector('#terms')) (document.querySelector('#terms') as HTMLElement).style.display = `none`;
  };

  const getGoogleRecaptchaToken = async () => {
    const token = await loadGoogloRecaptcha();
    setRecaptchaToken(token);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (submitButtonDisabled) {
      if (errors?.email?.find(i => i?.field === 'email')?.message || !email) {
        setEmailError(t('ftu:enter_edu_email'));
      }
      if (errors?.password?.find(i => i?.field === 'password')?.message || !password) {
        setPasswardError(t('ftu:create_edu_password'));
      }
      return;
    }
    setErrorRes('');
    if (!!clickTrack) clickTrack();
    let adsParams = JSON.parse(getCookie('ads') || '{}');
    let device_id = getCookie(Constants.B_COOKIE);
    let appDeviceId = getCookie(Constants.DEVICE_ID);
    const deviceId = entry === 'student-benefits' && isSayWeeeApp() ? appDeviceId : device_id;
    let timeStamp = Math.floor(new Date().getTime() / 1000);
    let sign = md5(`${email}${deviceId}${timeStamp}${AUTH_SIGN}`);
    let school = !!schoolData
      ? {
          channel: router.query.id || '',
          page_url: buildUrlWithOrigin(schoolData?.activity_school_url)
        }
      : {};
    const res = await emailRegisterByStudent(
      {
        ...adsParams,
        email,
        password,
        timestamp: timeStamp,
        referrer_id: getReferrerId(),
        referral_id: getReferralIdFromCookie(),
        recaptcha_response: recaptchaToken,
        ...school,
        ...reqParams
      },
      {
        headers: {
          sign
        }
      }
    );
    const redirect = getSafeRedirectUrl((router.query?.redirect_url as string) || (res?.object?.next_url as string));
    let addRedirectUrl = '';
    if (!!redirect) {
      addRedirectUrl = `&redirect_url=${encodeURIComponent(redirect)}`;
    }
    if (res?.result) {
      afterUserRegisterLogin(res?.object?.token, res?.object?.language);
      afterUserSign('email');
      if (res.object.is_edu_email_check) {
        window.location.href = `/${lang}/student-discount/code-verify?email=${email}${addRedirectUrl}`;
      }
      return;
    }
    window.sessionStorage.setItem(LoginSessionstoragekey.login_key, res?.message as string);
    if (res?.message_id === 'CSM10031') {
      // 学生权益流程中使用popup做安全校验，不跳转页面；
      if (entry === 'student-benefits') {
        codeRef.current?.show();
        return;
      } else {
        // 注册被拦截
        window.location.href = `/${lang}/account/verify?email=${encodeURIComponent(
          email
        )}&type=signup${addRedirectUrl}`;
        return;
      }
    }
    setErrorRes(res?.message);
  };

  useEffect(() => {
    getGoogleRecaptchaToken();
  }, []);

  return (
    <form onSubmit={handleSubmit} noValidate className={twMerge('flex flex-col gap-3', className)}>
      <div>
        {getFieldDecorator('email', {
          rules: [
            {
              required: true,
              message: t('std_email_error_msg'),
              pattern: new RegExp(EDU_EMAIL_REG)
            }
          ],
          initialValue: ''
        })(
          <StudentInputItem
            id="emailInputId"
            value={email || ''}
            onChange={val => {
              setErrorRes('');
            }}
            onFocus={() => {
              handleFocus();
              setEmailError('');
            }}
            onBlur={handleBlur}
            hasError={!!emailError}
            outerClass={'rounded-full'}
            formStyle={formStyle}
            placeholder={t('ftu:enter_edu_email')}
            label={t('ftu:enter_edu_email')}
            type="email"
          />
        )}
        {!!emailError && (
          <div className="text-left enki-body-3xs-medium text-input-200-fg-critical pt-2">{emailError}</div>
        )}
      </div>

      <div>
        {getFieldDecorator('password', {
          rules: [{ required: true, message: t('valid_passoword') }],
          initialValue: ''
        })(
          <StudentInputItem
            id="passwordInputId"
            value={password}
            onChange={val => {
              setErrorRes('');
            }}
            onFocus={() => {
              handleFocus();
              setPasswardError('');
            }}
            onBlur={handleBlur}
            hasError={!!passwordError}
            outerClass={'rounded-full'}
            formStyle={formStyle}
            placeholder={t('ftu:create_edu_password')}
            label={t('ftu:create_edu_password')}
            type={encodePassword ? 'password' : 'text'}
            suffix={
              <span className="inline-flex items-center justify-center" onClick={() => setEncodePassword(v => !v)}>
                {encodePassword ? <InvisibleFilled fontSize={16} /> : <VisibleFilled fontSize={16} />}
              </span>
            }
          />
        )}
        {!!passwordError && (
          <div className="text-left enki-body-3xs-medium text-input-200-fg-critical pt-2">{passwordError}</div>
        )}
      </div>

      <div>
        <div className="mb-2 text-left enki-body-3xs-medium text-input-200-fg-critical">{errorRes}</div>
        <Button
          type="primary"
          htmltype="submit"
          className={twMerge('h-12 !text-lg', formStyle === 'light' && 'bg-btn-secondary-bg text-white')}>
          {t('std_signup_and_verify')}
        </Button>
      </div>
      <VerifyEmailModal
        ref={codeRef}
        email={email}
        isSign={true}
        entry={entry}
        source={reqParams?.source}
        onSuccess={successRes => {
          onSuccess?.(successRes?.object);
          codeRef.current?.hide();
        }}
      />
    </form>
  );
};
